import apiClient from './client';
import { buildQueryString, type BaseListParams } from './utils';
import type { CohortWithDetails, CreateCohortRequest, UpdateCohortRequest, CohortStatus, PaginatedResponse, SessionFrequency, PreferredDay, PreferredTime, CohortTeamRole, CohortOverviewStats, HealthSummaryResponse, DismissAlertRequest, CohortEmailSettings, ParticipantFlagWithDetails, AttendanceDrillDownResponse, SatisfactionDrillDownResponse, ModalityProgressCard } from '../../types';

export interface UpdateSessionConfigRequest {
  session_count?: number;
  session_frequency?: SessionFrequency;
  preferred_days?: PreferredDay[];
  preferred_times?: PreferredTime[];
}

export interface SessionDateUpdate {
  session_number: number;
  scheduled_date: string;
}

export interface TeamAssignmentUpdate {
  role: CohortTeamRole;
  user_id: string | null;
}

export interface DateConstraintUpdate {
  start_date: string;
  end_date: string;
  label?: string;
}

export interface SessionUpdate {
  title?: string;
  scheduled_start?: string;
  scheduled_end?: string;
  status?: 'scheduled' | 'completed' | 'cancelled';
  zoom_link?: string | null;
  /** MED-780: Free-text physical location surfaced on the calendar invite. */
  location?: string | null;
  /** Optional message to include in calendar update notification (MED-71) */
  reschedule_message?: string;
}

export interface ModalityUpdate {
  modality_type: string;
  session_count?: number | null;
  session_frequency?: string | null;
  session_duration_minutes?: number | null;
  configuration?: Record<string, unknown>;
  // MED-848 — destructive replace path also accepts the new fields so that
  // when duration changes force a full modality rebuild, sub-type and AI
  // toggle are preserved on the freshly created rows.
  coaching_sub_type?: import('../../types').CoachingSubType | null;
  ai_features_enabled?: boolean;
  // MED-948 (E1.10a) — preserved across the destructive rebuild so a duration
  // change does not reset the Live Session Delivery gate.
  live_session_delivery_enabled?: boolean;
  // MED-1069 (E1.10c) — preserved across the destructive rebuild so a duration
  // change does not reset Commitment Tracking (group_coaching only).
  commitment_tracking_enabled?: boolean;
}

export interface CohortListParams extends BaseListParams {
  status?: CohortStatus | CohortStatus[];
  partnerId?: string;
  programId?: string;
}

/**
 * MED-1196 / B3: per-store row counts returned by the cohort AI-data purge.
 * Mirrors the API's CohortAiDataPurgeResult.
 */
export interface CohortAiDataPurgeResult {
  cohortId: string;
  sessionCount: number;
  deleted: {
    transcripts: number;
    airtime: number;
    themes: number;
    recapDrafts: number;
  };
  totalDeleted: number;
}

export const cohortsApi = {
  /**
   * Get all cohorts with optional filtering and pagination
   */
  list: async (params?: CohortListParams): Promise<PaginatedResponse<CohortWithDetails>> => {
    const url = `/api/cohorts${buildQueryString(params)}`;
    return apiClient.getPaginated<CohortWithDetails>(url);
  },

  /**
   * Get a single cohort by ID
   */
  getById: async (id: string): Promise<CohortWithDetails> => {
    return apiClient.get<CohortWithDetails>(`/api/cohorts/${id}`);
  },

  /**
   * Create a new cohort
   */
  create: async (data: CreateCohortRequest): Promise<CohortWithDetails> => {
    return apiClient.post<CohortWithDetails>('/api/cohorts', data);
  },

  /**
   * Update a cohort
   */
  update: async (id: string, data: UpdateCohortRequest): Promise<CohortWithDetails> => {
    return apiClient.put<CohortWithDetails>(`/api/cohorts/${id}`, data);
  },

  /**
   * Delete a cohort (soft delete)
   */
  delete: async (id: string): Promise<void> => {
    return apiClient.delete<void>(`/api/cohorts/${id}`);
  },

  /**
   * Check if a cohort code is available
   */
  checkCode: async (code: string): Promise<{ available: boolean }> => {
    return apiClient.get<{ available: boolean }>(`/api/cohorts/check-code/${code}`);
  },

  /**
   * Check if a cohort name and/or short name is available (case-insensitive)
   */
  checkAvailability: async (params: { name?: string; shortName?: string }): Promise<{ nameAvailable?: boolean; shortNameAvailable?: boolean }> => {
    const query = new URLSearchParams();
    if (params.name) query.set('name', params.name);
    if (params.shortName) query.set('short_name', params.shortName);
    return apiClient.get<{ nameAvailable?: boolean; shortNameAvailable?: boolean }>(`/api/cohorts/check-availability?${query.toString()}`);
  },

  /**
   * Get cohort counts by status
   */
  getStatusCounts: async (partnerId?: string): Promise<Record<CohortStatus, number>> => {
    const queryParams = partnerId ? `?partnerId=${partnerId}` : '';
    return apiClient.get<Record<CohortStatus, number>>(`/api/cohorts/status-counts${queryParams}`);
  },

  /**
   * Update session configuration (count, frequency, preferred days/times)
   */
  updateSessionConfig: async (id: string, data: UpdateSessionConfigRequest): Promise<CohortWithDetails> => {
    return apiClient.put<CohortWithDetails>(`/api/cohorts/${id}/session-config`, data);
  },

  /**
   * Update session dates. MED-1360: regenerateDueDates false is the CM's No,
   * so the API keeps the task due dates as they are.
   */
  updateSessions: async (
    id: string,
    sessions: SessionDateUpdate[],
    regenerateDueDates?: boolean
  ): Promise<CohortWithDetails> => {
    return apiClient.put<CohortWithDetails>(`/api/cohorts/${id}/sessions`, {
      sessions,
      ...(regenerateDueDates === undefined ? {} : { regenerate_due_dates: regenerateDueDates }),
    });
  },

  /**
   * Update an individual session
   */
  updateSession: async (
    cohortId: string,
    sessionId: string,
    data: SessionUpdate,
  ): Promise<import('../../types').Session & { missClaim?: import('../../types').MissClaim | null }> => {
    // MED-1346: a 1:1 moved within 24 hours of its start returns a pending missClaim.
    return apiClient.patch(`/api/cohorts/${cohortId}/sessions/${sessionId}`, data);
  },

  /**
   * MED-1350: return a dated 1:1 or check-in to unscheduled. The server cancels
   * a Calendly booking or calendar invite first, and keeps the date (409) when
   * that cancel fails. Staff, or the coach of the session.
   */
  clearSessionDate: async (
    cohortId: string,
    sessionId: string,
    /** MED-1346 F12: the drawer "Cancel session" sends cancel, so history logs "Canceled". */
    body: { cancel?: true } = {},
  ): Promise<{
    id: string;
    status: 'unscheduled';
    scheduled_start: null;
    scheduled_end: null;
    /** MED-1346: set when the cancel came within 24 hours of the start. */
    missClaim?: import('../../types').MissClaim | null;
  }> => {
    return apiClient.post(`/api/cohorts/${cohortId}/sessions/${sessionId}/clear-date`, body);
  },

  /**
   * MED-866 / E1.12: Set AI Listening for a session as a Cohort Manager / Admin.
   * Hits the dedicated CM endpoint (separate from updateSession so it never
   * triggers calendar re-sync / attendee notifications). Server enforces the
   * CM/Admin guard (AC8) and the 15-minute lock (AC10).
   */
  setSessionAiListening: async (
    cohortId: string,
    sessionId: string,
    enabled: boolean,
  ): Promise<{ ai_listening_enabled: boolean; ai_listening_locked: boolean }> => {
    return apiClient.patch<{ ai_listening_enabled: boolean; ai_listening_locked: boolean }>(
      `/api/cohorts/${cohortId}/sessions/${sessionId}/ai-listening`,
      { enabled },
    );
  },

  /**
   * MED-1196 / B3: Purge all AI-generated data for a cohort (internal admin
   * only). Deletes every persisted AI-output store (transcripts, airtime,
   * themes, recap drafts) across all sessions. Server enforces the
   * internal_admin guard (AC1) and the live_session_delivery flag gate (AC6);
   * idempotent when there is nothing to purge (AC5).
   */
  purgeAiData: async (id: string): Promise<CohortAiDataPurgeResult> => {
    return apiClient.post<CohortAiDataPurgeResult>(`/api/cohorts/${id}/purge-ai-data`);
  },

  /**
   * Update team assignments
   */
  updateTeamAssignments: async (id: string, assignments: TeamAssignmentUpdate[]): Promise<CohortWithDetails> => {
    return apiClient.put<CohortWithDetails>(`/api/cohorts/${id}/team-assignments`, { assignments });
  },

  /**
   * Update date constraints
   */
  updateDateConstraints: async (id: string, constraints: DateConstraintUpdate[]): Promise<CohortWithDetails> => {
    return apiClient.put<CohortWithDetails>(`/api/cohorts/${id}/date-constraints`, { constraints });
  },

  /**
   * Update primary partner contacts
   */
  updatePrimaryContacts: async (id: string, contactIds: string[]): Promise<CohortWithDetails> => {
    return apiClient.put<CohortWithDetails>(`/api/cohorts/${id}/primary-contacts`, { contact_ids: contactIds });
  },

  /**
   * Duplicate a cohort
   */
  duplicate: async (id: string, name?: string): Promise<CohortWithDetails> => {
    return apiClient.post<CohortWithDetails>(`/api/cohorts/${id}/duplicate`, name ? { name } : {});
  },

  /**
   * Get task counts for a cohort
   */
  getTaskCount: async (id: string): Promise<{ total: number; templateTasks: number; manualTasks: number }> => {
    return apiClient.get<{ total: number; templateTasks: number; manualTasks: number }>(`/api/cohorts/${id}/tasks/count`);
  },

  /**
   * Regenerate tasks from templates for a cohort
   * @param mode - 'skip_existing' (default) or 'replace_template_tasks'
   */
  regenerateTasks: async (id: string, mode?: 'skip_existing' | 'replace_template_tasks'): Promise<{ tasksGenerated: number; tasksDeleted: number; mode: string }> => {
    return apiClient.post<{ tasksGenerated: number; tasksDeleted: number; mode: string }>(`/api/cohorts/${id}/regenerate-tasks`, { mode });
  },

  /**
   * Get phases applicable to this cohort based on its type and modalities
   */
  getPhases: async (id: string): Promise<import('../../types').TaskPhaseRecord[]> => {
    return apiClient.get<import('../../types').TaskPhaseRecord[]>(`/api/cohorts/${id}/phases`);
  },

  /**
   * Get overview statistics for cohort detail page
   * Includes sessions, participants, groups, tasks, milestones, and recent activity
   */
  getOverviewStats: async (id: string): Promise<CohortOverviewStats> => {
    return apiClient.get<CohortOverviewStats>(`/api/cohorts/${id}/overview-stats`);
  },

  /**
   * Update modalities for a cohort
   * Used to update session configuration including duration
   */
  updateModalities: async (id: string, modalities: ModalityUpdate[]): Promise<CohortWithDetails> => {
    return apiClient.put<CohortWithDetails>(`/api/cohorts/${id}/modalities`, { modalities });
  },

  /**
   * Validate announcement prerequisites for a modality (MED-563 E18-5)
   */
  getAnnouncePrerequisites: async (
    cohortId: string,
    modalityId: string,
  ): Promise<{ valid: boolean; errors: string[] }> => {
    return apiClient.get<{ valid: boolean; errors: string[] }>(
      `/api/cohorts/${cohortId}/modalities/${modalityId}/announce-prerequisites`,
    );
  },

  /**
   * Announce a modality to participants (MED-563 E18-5)
   * sendInvites: true = send SGOS magic link emails; false = skip invites
   */
  announceModality: async (
    cohortId: string,
    modalityId: string,
    sendInvites: boolean,
  ): Promise<{ modality: import('../../types').Modality; emailsSent: number }> => {
    return apiClient.post<{ modality: import('../../types').Modality; emailsSent: number }>(
      `/api/cohorts/${cohortId}/modalities/${modalityId}/announce`,
      { sendInvites },
    );
  },

  /**
   * MED-848 — patch a single modality's coaching sub-type and/or AI features
   * toggle without rebuilding the modality. Used by the Edit Cohort flow.
   * Backend rejects sub-type changes once invites_sent=true (AC9 lock).
   */
  updateModalitySettings: async (
    cohortId: string,
    modalityId: string,
    settings: {
      coaching_sub_type?: import('../../types').CoachingSubType | null;
      ai_features_enabled?: boolean;
      // MED-948 (E1.10a) — Live Session Delivery gate (AC7). When set false the
      // backend also resets ai_features_enabled to false (AC8).
      live_session_delivery_enabled?: boolean;
      // MED-1069 (E1.10c) — Commitment Tracking (group_coaching). Independent of
      // the Live Session Delivery gate; the backend does NOT reset it.
      commitment_tracking_enabled?: boolean;
    },
  ): Promise<import('../../types').Modality> => {
    return apiClient.patch<import('../../types').Modality>(
      `/api/cohorts/${cohortId}/modalities/${modalityId}/settings`,
      settings,
    );
  },

  /**
   * Validate announcement prerequisites for ALL unannounced modalities (MED-604)
   */
  getAnnounceAllPrerequisites: async (
    cohortId: string,
  ): Promise<{
    modalities: Array<{
      modalityId: string;
      modalityType: import('../../types').ModalityType;
      label: string;
      ready: boolean;
      errors: string[];
    }>;
    allReady: boolean;
    readyCount: number;
    totalCount: number;
  }> => {
    return apiClient.get(`/api/cohorts/${cohortId}/announce-all-prerequisites`);
  },

  /**
   * Announce all ready modalities in a cohort simultaneously (MED-604)
   */
  announceAllModalities: async (
    cohortId: string,
    sendInvites: boolean,
    modalityIds?: string[],
  ): Promise<{
    announcedModalities: import('../../types').Modality[];
    emailsSent: number;
    skippedModalities: string[];
  }> => {
    return apiClient.post(`/api/cohorts/${cohortId}/announce-all`, {
      sendInvites,
      ...(modalityIds ? { modalityIds } : {}),
    });
  },

  // ── Announcement Email Template (MED-645 E18-16) ──────────────────────────

  /**
   * Get default announcement email template with merge tag definitions
   */
  getAnnouncementEmailTemplate: async (
    cohortId: string,
  ): Promise<{
    subject: string;
    body: string;
    mergeTagDefinitions: Array<{
      tag: string;
      label: string;
      category: 'participant' | 'partner' | 'cohort_config' | 'system';
      description: string;
    }>;
  }> => {
    return apiClient.get(`/api/cohorts/${cohortId}/announcement-email/template`);
  },

  /**
   * Preview announcement email with resolved merge tags
   */
  previewAnnouncementEmail: async (
    cohortId: string,
    subject: string,
    body: string,
  ): Promise<{
    subject: string;
    body: string;
    unresolvableTags: string[];
    sampleParticipant: { first_name: string; email: string };
    recipientCount: number;
  }> => {
    return apiClient.post(`/api/cohorts/${cohortId}/announcement-email/preview`, {
      subject,
      body,
    });
  },

  /**
   * Send announcement emails to all active participants
   */
  sendAnnouncementEmails: async (
    cohortId: string,
    subject: string,
    body: string,
  ): Promise<{ emailsSent: number; errors: number }> => {
    return apiClient.post(`/api/cohorts/${cohortId}/announcement-email/send`, {
      subject,
      body,
    });
  },

  /**
   * Send a test announcement email to the logged-in CM's email
   */
  sendTestAnnouncementEmail: async (
    cohortId: string,
    subject: string,
    body: string,
  ): Promise<{ email: string }> => {
    return apiClient.post(`/api/cohorts/${cohortId}/announcement-email/test`, {
      subject,
      body,
    });
  },

  /**
   * Regenerate task due dates for a cohort after date changes
   * Only updates tasks that:
   * - Have a template reference
   * - Are not manually edited (due_date_manually_set = false)
   * - Are not complete or cancelled
   */
  regenerateDueDates: async (id: string): Promise<{
    updatedCount: number;
    eligibleCount: number;
    skippedManualCount: number;
    createdCount?: number;
  }> => {
    return apiClient.post<{
      updatedCount: number;
      eligibleCount: number;
      skippedManualCount: number;
      createdCount?: number;
    }>(`/api/cohorts/${id}/regenerate-due-dates`, {});
  },

  /**
   * Regenerate task assignees after team assignment changes.
   * Only updates auto-assigned tasks whose job title matches a changed role.
   */
  regenerateTeamAssignments: async (
    id: string,
    changedJobTitleIds: string[],
  ): Promise<{
    updatedCount: number;
    eligibleCount: number;
    skippedManualCount: number;
  }> => {
    return apiClient.post<{
      updatedCount: number;
      eligibleCount: number;
      skippedManualCount: number;
    }>(`/api/cohorts/${id}/regenerate-team-assignments`, { changedJobTitleIds });
  },

  // ============================================================================
  // MED-160: Cohort Status Progression API Methods
  // ============================================================================

  /**
   * Get status readiness stats for a cohort
   * Returns completion percentage for current status phase and whether ready to advance
   */
  getStatusReadiness: async (id: string): Promise<StatusReadinessResponse> => {
    return apiClient.get<StatusReadinessResponse>(`/api/cohorts/${id}/status-readiness`);
  },

  /**
   * Advance cohort to the next status (requires >=90% completion)
   */
  advanceStatus: async (id: string): Promise<StatusChangeResponse> => {
    return apiClient.post<StatusChangeResponse>(`/api/cohorts/${id}/advance-status`, {});
  },

  /**
   * Revert cohort to the previous status
   */
  revertStatus: async (id: string): Promise<StatusChangeResponse> => {
    return apiClient.post<StatusChangeResponse>(`/api/cohorts/${id}/revert-status`, {});
  },

  /**
   * Cancel a cohort (hides from active lists, can be restored)
   */
  cancelCohort: async (id: string): Promise<StatusChangeResponse> => {
    return apiClient.post<StatusChangeResponse>(`/api/cohorts/${id}/cancel`, {});
  },

  /**
   * Restore a cancelled cohort to its previous status
   */
  restoreCohort: async (id: string): Promise<StatusChangeResponse> => {
    return apiClient.post<StatusChangeResponse>(`/api/cohorts/${id}/restore`, {});
  },

  /**
   * Archive a cohort (permanent, cannot be undone)
   * Only available for completed or cancelled cohorts
   */
  archiveCohort: async (id: string): Promise<StatusChangeResponse> => {
    return apiClient.post<StatusChangeResponse>(`/api/cohorts/${id}/archive`, {});
  },

  // ============================================================================
  // MED-114: Cohort Health Monitoring
  // ============================================================================

  /**
   * Get modality progress summary for cohort dashboard (MED-570)
   */
  getModalityProgress: async (id: string): Promise<ModalityProgressCard[]> => {
    return apiClient.get<ModalityProgressCard[]>(`/api/cohorts/${id}/modality-progress`);
  },

  /**
   * Get health summary with alerts for a cohort
   */
  getHealthSummary: async (id: string): Promise<HealthSummaryResponse> => {
    return apiClient.get<HealthSummaryResponse>(`/api/cohorts/${id}/health-summary`);
  },

  /** MED-425: Get attendance drill-down data */
  getAttendanceDrillDown: async (id: string): Promise<AttendanceDrillDownResponse> => {
    return apiClient.get<AttendanceDrillDownResponse>(`/api/cohorts/${id}/attendance-drilldown`);
  },

  /** MED-426: Get satisfaction drill-down data */
  getSatisfactionDrillDown: async (id: string): Promise<SatisfactionDrillDownResponse> => {
    return apiClient.get<SatisfactionDrillDownResponse>(`/api/cohorts/${id}/satisfaction-drilldown`);
  },

  /**
   * Dismiss a health alert
   */
  dismissHealthAlert: async (cohortId: string, data: DismissAlertRequest): Promise<void> => {
    return apiClient.post<void>(`/api/cohorts/${cohortId}/health-alerts/dismiss`, data);
  },

  addressHealthAlert: async (
    cohortId: string,
    data: { ruleKey: string; entityId: string; note?: string; triggerSnapshot?: Record<string, unknown> },
  ): Promise<void> => {
    return apiClient.post<void>(`/api/cohorts/${cohortId}/health-alerts/address`, data);
  },

  /**
   * Dismiss an on-track improvement item (MED-418)
   */
  dismissImprovementAlert: async (cohortId: string, historyId: string): Promise<void> => {
    return apiClient.post<void>(`/api/cohorts/${cohortId}/health-alerts/dismiss-improvement`, { historyId });
  },

  /**
   * Restore a dismissed/addressed health alert (MED-534)
   */
  restoreHealthAlert: async (
    cohortId: string,
    data: { ruleKey: string; entityId: string },
  ): Promise<void> => {
    return apiClient.post<void>(`/api/cohorts/${cohortId}/health-alerts/restore`, data);
  },

  // ============================================================================
  // Participant Flags (MED-446)
  // ============================================================================

  getParticipantFlags: async (cohortId: string): Promise<ParticipantFlagWithDetails[]> => {
    return apiClient.get<ParticipantFlagWithDetails[]>(`/api/cohorts/${cohortId}/flags`);
  },

  getParticipantFlagsForParticipant: async (
    cohortId: string,
    participantId: string,
  ): Promise<ParticipantFlagWithDetails[]> => {
    return apiClient.get<ParticipantFlagWithDetails[]>(
      `/api/cohorts/${cohortId}/flags/participant/${participantId}`,
    );
  },

  getSessionFlags: async (cohortId: string, sessionId: string): Promise<ParticipantFlagWithDetails[]> => {
    return apiClient.get<ParticipantFlagWithDetails[]>(
      `/api/cohorts/${cohortId}/flags/session/${sessionId}`,
    );
  },

  dismissParticipantFlag: async (cohortId: string, flagId: string): Promise<ParticipantFlagWithDetails> => {
    return apiClient.patch<ParticipantFlagWithDetails>(`/api/cohorts/${cohortId}/flags/${flagId}/dismiss`);
  },

  addressParticipantFlag: async (
    cohortId: string,
    flagId: string,
    note?: string,
  ): Promise<ParticipantFlagWithDetails> => {
    return apiClient.patch<ParticipantFlagWithDetails>(`/api/cohorts/${cohortId}/flags/${flagId}/address`, {
      note,
    });
  },

  // ============================================================================
  // MED-438: Granular Email Settings
  // ============================================================================

  /**
   * Get granular email settings for a cohort
   */
  getEmailSettings: async (id: string): Promise<CohortEmailSettings> => {
    return apiClient.get<CohortEmailSettings>(`/api/cohorts/${id}/email-settings`);
  },

  /**
   * Update granular email settings for a cohort
   */
  updateEmailSettings: async (id: string, settings: CohortEmailSettings): Promise<CohortEmailSettings> => {
    return apiClient.put<CohortEmailSettings>(`/api/cohorts/${id}/email-settings`, settings);
  },

  /**
   * MED-1307: the cohort's sender address, its default, and the allowed domains
   */
  getSenderEmail: async (id: string): Promise<SenderEmailSettings> => {
    return apiClient.get<SenderEmailSettings>(`/api/cohorts/${id}/sender-email`);
  },

  /**
   * MED-1307: set or clear the cohort's sender address. Rejects with the API
   * message when the domain is not allowed.
   */
  updateSenderEmail: async (id: string, senderEmail: string | null): Promise<SenderEmailSettings> => {
    return apiClient.put<SenderEmailSettings>(`/api/cohorts/${id}/sender-email`, {
      sender_email: senderEmail,
    });
  },

  // ============================================================================
  // MED-778: Audience Feature Toggles
  // ============================================================================

  /**
   * Get feature toggle state for a cohort
   */
  getFeatureToggles: async (id: string): Promise<FeatureToggles> => {
    return apiClient.get<FeatureToggles>(`/api/cohorts/${id}/feature-toggles`);
  },

  /**
   * Update feature toggles for a cohort
   */
  updateFeatureToggles: async (id: string, toggles: Partial<FeatureToggles>): Promise<FeatureToggles> => {
    return apiClient.patch<FeatureToggles>(`/api/cohorts/${id}/feature-toggles`, toggles);
  },
};

export interface FeatureToggles {
  participant_features_enabled: boolean;
  coach_features_enabled: boolean;
  /** MED-1307 (MDF-184): raw column. Has effect only when participant_features_enabled is true. */
  participant_app_access_enabled: boolean;
}

// MED-1307: per-cohort sender address
export interface SenderEmailSettings {
  /** Null means the cohort sends from default_sender_email */
  sender_email: string | null;
  default_sender_email: string;
  allowed_domains: string[];
}

// MED-160: Response types for status progression
export interface StatusReadinessResponse {
  currentStatus: CohortStatus;
  tasksCompleted: number;
  tasksTotal: number;
  completionPercent: number;
  readyToAdvance: boolean;
  nextStatus: CohortStatus | null;
  previousStatus: CohortStatus | null;
  overallHealth: 'healthy' | 'warning' | 'critical' | 'no_data';
}

export interface StatusChangeResponse {
  cohort: CohortWithDetails;
  previousStatus: CohortStatus;
  newStatus: CohortStatus;
  action: 'advance' | 'revert' | 'cancel' | 'restore' | 'archive';
}

export default cohortsApi;
